import { resolveStationRef } from './resolveStationRef'
import { clusterTimetable } from './clusterTimetable'
import type {
  SchemaData,
  SchemaLine,
  SchemaStation,
  SchemaStationTimetable,
} from '../../schema/types'

/**
 * The station page's `ready`-state inputs, derivable from the schema —
 * everything the template needs that is NOT fetched live (arrivals,
 * status, editorial content have their own loaders).
 *
 * Soft-404 contract (GSC 2026-08-17): `schema.json` is 28.9 MB, and the
 * SPA mount hides the prerendered `<main>` before that download can
 * finish — Googlebot's batch renderer snapshots the blank in-between
 * state and classifies the page Soft 404 (1,478 URLs by 2026-08-15).
 * The prerender therefore embeds this payload into the static HTML
 * (`serializeStationPageData`) and `StationPage` reads it back
 * synchronously on first render (`readEmbeddedStationPageData`), so a
 * prerendered page reaches `ready` without any schema download. Both
 * sides go through {@link buildStationPageData} so they cannot drift.
 */
export interface StationPageData {
  station: SchemaStation
  timetable: SchemaStationTimetable | undefined
  otherLines: readonly SchemaLine[]
}

/** DOM id of the embedded-payload `<script type="application/json">`. */
export const STATION_PAGE_DATA_ELEMENT_ID = 'station-page-data'

/**
 * Resolves `stationId` (NAPTAN id / slug / schema id, `?line-id=`
 * override) and assembles the page data: the resolved cluster record,
 * the cluster-merged timetable, and one entry per *other* line serving
 * the cluster. Returns `null` for an unknown reference.
 */
export function buildStationPageData(
  schema: SchemaData,
  stationId: string,
  lineId: string | undefined,
): StationPageData | null {
  const station = resolveStationRef(schema, stationId, lineId)
  if (station === null) return null
  // Cluster siblings, one per line, minus the page's own line.
  const seenLineIds = new Set<string>([station.line?.id ?? ''])
  const otherLines: SchemaLine[] = []
  if (station.naptanId !== undefined) {
    for (const s of schema.stations) {
      if (s.naptanId !== station.naptanId) continue
      const line = s.line
      if (line === undefined || seenLineIds.has(line.id)) continue
      seenLineIds.add(line.id)
      otherLines.push(line)
    }
  }
  return { station, timetable: clusterTimetable(schema, station), otherLines }
}

interface EmbeddedEnvelope {
  stationId: string
  data: StationPageData
}

/**
 * Serializes the payload as an inert JSON `<script>` for the prerendered
 * HTML. `<` is escaped so station data can never terminate the script
 * element early (`</script>` injection).
 */
export function serializeStationPageData(stationId: string, data: StationPageData): string {
  const envelope: EmbeddedEnvelope = { stationId, data }
  const json = JSON.stringify(envelope).replace(/</g, '\\u003c')
  return `<script type="application/json" id="${STATION_PAGE_DATA_ELEMENT_ID}">${json}</script>`
}

/**
 * Reads the embedded payload back, or `null` when it is absent or does
 * not match the route: the prerender bakes the default cluster pick for
 * exactly one station, so a different `stationId` or any `?line-id=`
 * override must fall back to the schema fetch.
 */
export function readEmbeddedStationPageData(
  stationId: string,
  lineId: string | undefined,
): StationPageData | null {
  if (lineId !== undefined) return null
  const el = document.getElementById(STATION_PAGE_DATA_ELEMENT_ID)
  if (el === null) return null
  try {
    const envelope = JSON.parse(el.textContent ?? '') as EmbeddedEnvelope
    if (envelope.stationId !== stationId) return null
    return envelope.data
  } catch {
    return null
  }
}
